import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Space, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

// API 基础地址 — 生产环境用相对路径，开发环境用 localhost
const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:18080');

const CustomerServiceConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [wechatQrCode, setWechatQrCode] = useState('');

  // 获取完整图片 URL（管理员前端需要完整路径）
  const getAdminImageUrl = (url) => {
    if (!url) return '';
    // Docker 内部地址 → 转换为 /uploads/ 代理路径
    if (url.includes('hk-api:8080/uploads/')) {
      const idx = url.indexOf('/uploads/');
      return url.substring(idx);
    }
    // 如果已经是外部可访问的完整 URL，直接返回
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // 相对路径：添加 API 基础地址
    return `${API_BASE}${url}`;
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/configs/customer-service`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.code === 0) {
        form.setFieldsValue({
          telegram: data.data.telegram,
          whatsapp: data.data.whatsapp,
          email: data.data.email,
          skype: data.data.skype,
          wechat: data.data.wechat,
          zalo: data.data.zalo,
        });
        setWechatQrCode(data.data.wechat_qr_code || '');
      }
    } catch (error) {
      message.error('获取配置失败');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/configs/customer-service`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...values,
          wechat_qr_code: wechatQrCode,
        }),
      });
      const data = await res.json();
      if (data.code === 0) {
        message.success('保存成功');
      } else {
        message.error(data.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      const resData = await res.json();
      if (resData.code === 0) {
        const url = resData.data?.url;
        setWechatQrCode(url);
        message.success('上传成功');
      } else {
        message.error(resData.message || '上传失败');
      }
    } catch (error) {
      message.error('上传失败');
    }
    return false;
  };

  return (
    <div>
      <Card
        title="客服联系方式配置"
        extra={
          <span style={{ color: '#999', fontSize: '12px' }}>
            配置用户端悬浮客服按钮的联系方式
          </span>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            telegram: '',
            whatsapp: '',
            email: '',
            skype: '',
            wechat: '',
            zalo: '',
          }}
        >
          <Form.Item
            label="Telegram"
            name="telegram"
            extra="输入 Telegram 用户名或链接，如：@username 或 https://t.me/username"
          >
            <Input placeholder="@username 或 https://t.me/username" />
          </Form.Item>

          <Form.Item
            label="WhatsApp"
            name="whatsapp"
            extra="输入 WhatsApp 号码或链接，如：+1234567890"
          >
            <Input placeholder="+1234567890" />
          </Form.Item>

          <Form.Item
            label="Skype"
            name="skype"
            extra="输入 Skype 用户名"
          >
            <Input placeholder="live:username" />
          </Form.Item>

          <Form.Item
            label="微信"
            name="wechat"
            extra="输入微信号"
          >
            <Input placeholder="wechat_id" />
          </Form.Item>

          <Form.Item label="微信客服二维码">
            <div style={{ marginBottom: 12 }}>
              <Upload
                beforeUpload={handleUpload}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />}>上传二维码图片</Button>
              </Upload>
              <span style={{ marginLeft: 12, color: '#999', fontSize: 12 }}>
                支持 JPG、PNG 格式，建议尺寸 200x200
              </span>
            </div>
            {wechatQrCode ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={getAdminImageUrl(wechatQrCode)}
                  alt="微信二维码"
                  width={160}
                  height={160}
                  style={{ borderRadius: 8, border: '1px solid #f0f0f0', objectFit: 'cover' }}
                />
                <Button
                  size="small"
                  danger
                  style={{ position: 'absolute', top: -8, right: -8 }}
                  onClick={() => setWechatQrCode('')}
                >
                  删除
                </Button>
              </div>
            ) : (
              <div style={{ width: 160, height: 160, borderRadius: 8, border: '1px dashed #d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                暂无二维码
              </div>
            )}
          </Form.Item>

          <Form.Item
            label="Zalo"
            name="zalo"
            extra="Zalo（越南常用通讯软件）"
          >
            <Input placeholder="Zalo ID" />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
            extra="显示在联系客服弹窗底部"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="support@example.com" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存配置
              </Button>
              <Button onClick={fetchConfig} disabled={initialLoading}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CustomerServiceConfig;
